import React, { useCallback, useState, useMemo } from 'react'
import Grid from '@material-ui/core/Grid'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import SydButton from '../../../../../../commonDesign/Button'
import SydModalActions from '../../../../../../commonDesign/SydModal/SydModalActions'
import SydLabel, { hookFormErrorAdapter } from '../../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../../commonDesign/SydInput'
import SydSelect from '../../../../../../commonDesign/SydSelect'
import Switch from '../../../../../../molecules/Switch'
import { useAddQueryBlueprintDefinition } from '../../../../../../../api/denali'
import { decipherError } from '../../../../../../../utils/decipherError'

// Simple identifier generator
const createIdentifier = (length) => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

function NewVersionForm ({ blueprint, allVersions, initialData, onCancel, onComplete }) {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { mutateAsync: addDefinition } = useAddQueryBlueprintDefinition()

  // Determine mode and defaults
  const mode = initialData ? 'parsed' // From Parse DQL
    : allVersions.length === 0 ? 'first'
      : allVersions.length === 1 ? 'copy-single'
        : 'copy-multiple'

  // Get default source version (latest by date)
  const defaultSourceVersion = useMemo(() => {
    if (mode === 'first' || mode === 'parsed') return null
    if (mode === 'copy-single') return allVersions[0]
    // For copy-multiple, find latest by date
    return [...allVersions].sort((a, b) =>
      new Date(b.versionDate) - new Date(a.versionDate)
    )[0]
  }, [mode, allVersions])

  const [selectedSourceVersion, setSelectedSourceVersion] = useState(defaultSourceVersion)

  // Source version options for selector
  const sourceVersionOptions = useMemo(() => {
    return (allVersions || []).map(v => {
      const dateStr = v.versionDate ? new Date(v.versionDate).toLocaleDateString() : ''
      const descStr = v.description ? ` - ${v.description}` : ''
      return {
        label: `${v.definitionVersionId}${descStr}${dateStr ? ` • ${dateStr}` : ''}`,
        value: v.definitionVersionId
      }
    })
  }, [allVersions])

  const form = useForm({
    defaultValues: {
      definitionVersionId: '',
      description: '',
      setAsActive: mode === 'first' // First version defaults to active
    },
    mode: 'onChange'
  })

  const handleSubmit = useCallback(async (formData) => {
    let data
    let presentation = null

    if (initialData) {
      // From Parse DQL - wrap in purposes structure
      data = {
        purposes: {
          [initialData.purposeCode]: initialData.purposeData
        }
      }
      // No presentation data from parsed DQL
      presentation = { options: {} }
    } else if (mode === 'first') {
      // First version - empty purposes and presentation
      data = { purposes: {} }
      presentation = { options: {} }
    } else {
      // Copy from source version
      const sourceVersion = mode === 'copy-single'
        ? allVersions[0]
        : allVersions.find(v => v.definitionVersionId === selectedSourceVersion?.definitionVersionId)

      data = sourceVersion ? JSON.parse(JSON.stringify(sourceVersion.data)) : { purposes: {} }
      // Also copy presentation data if it exists
      presentation = sourceVersion?.presentation
        ? JSON.parse(JSON.stringify(sourceVersion.presentation))
        : { options: {} }
    }

    const command = {
      blueprintId: blueprint.blueprintId,
      definition: {
        definitionVersionId: formData.definitionVersionId || undefined,
        description: formData.description || null,
        data,
        presentation
      },
      options: {
        setAsActive: formData.setAsActive
      }
    }

    try {
      setError(null)
      setProcessing(true)

      const result = await addDefinition(command)
      if (result?.statusCode === 500) {
        setError('Something unexpected happened while creating the version')
        return
      }
      onComplete(result)
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [blueprint.blueprintId, initialData, mode, allVersions, selectedSourceVersion, addDefinition, onComplete])

  const onSubmit = useCallback(async (e) => {
    const handler = form.handleSubmit(handleSubmit, (errors) => console.error(errors))
    await handler(e)
  }, [form, handleSubmit])

  // Generate random version ID
  const handleGenerateId = useCallback(() => {
    form.setValue('definitionVersionId', createIdentifier(16))
  }, [form])

  return (
    <Grid container spacing={2}>
      {/* Mode Indicator */}
      <Grid item xs={12}>
        {initialData && (
          <div style={{ padding: '8px', backgroundColor: '#e3f2fd', borderRadius: '4px', marginBottom: '8px' }}>
            Creating new version from parsed DQL query
          </div>
        )}
        {mode === 'first' && (
          <p>Creating the first version for this blueprint. Configure purposes after creation.</p>
        )}
        {mode === 'copy-single' && (
          <p>Creating new version by copying from: <strong>{allVersions[0]?.definitionVersionId}</strong></p>
        )}
      </Grid>

      {/* Source Version Selector (only for multiple versions) */}
      {mode === 'copy-multiple' && (
        <Grid item xs={12}>
          <SydLabel label='Copy From Version'>
            <SydSelect
              options={sourceVersionOptions}
              value={selectedSourceVersion?.definitionVersionId}
              onChange={e => {
                const version = allVersions.find(v => v.definitionVersionId === e.target.value)
                setSelectedSourceVersion(version)
              }}
            />
          </SydLabel>
        </Grid>
      )}

      {/* Version ID */}
      <Grid item xs={12} md={8}>
        <Controller
          control={form.control}
          name='definitionVersionId'
          rules={{
            maxLength: { value: 32, message: 'Maximum 32 Characters' }
          }}
          render={(f) => (
            <SydLabel
              label='Version ID'
              helperText='Leave empty to auto-generate'
              error={hookFormErrorAdapter(form, f.fieldState)}
            >
              <SydInput {...f.field} placeholder='Auto-generated if empty' />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <SydLabel label='&nbsp;'>
          <SydButton onClick={handleGenerateId} size='md' variant='ghost' icon='refresh'>
            Generate ID
          </SydButton>
        </SydLabel>
      </Grid>

      {/* Description */}
      <Grid item xs={12}>
        <Controller
          control={form.control}
          name='description'
          render={(f) => (
            <SydLabel label='Description' helperText='What changed in this version?'>
              <SydInput {...f.field} multiline rows={2} />
            </SydLabel>
          )}
        />
      </Grid>

      {/* Set as Active */}
      <Grid item xs={12}>
        <Controller
          control={form.control}
          name='setAsActive'
          render={(f) => (
            <SydLabel label='Set as Active Version'>
              <Switch
                checked={f.field.value}
                onChange={(e) => f.field.onChange(e.target.checked)}
              />
            </SydLabel>
          )}
        />
      </Grid>

      {/* Submit */}
      <Grid item xs={12}>
        <SydModalActions>
          {error ? <div className='__error'>{error}</div> : null}
          <SydButton disabled={processing} variant='ghost' size='md' onClick={onCancel}>
            Cancel
          </SydButton>
          <SydButton
            processing={processing}
            disabled={!form.formState.isValid}
            variant='primary'
            size='md'
            onClick={onSubmit}
          >
            Create Version
          </SydButton>
        </SydModalActions>
      </Grid>
    </Grid>
  )
}

NewVersionForm.propTypes = {
  blueprint: PropTypes.object.isRequired,
  allVersions: PropTypes.array,
  initialData: PropTypes.object,
  onComplete: PropTypes.func,
  onCancel: PropTypes.func
}

NewVersionForm.defaultProps = {
  allVersions: [],
  initialData: null
}

export default NewVersionForm
